import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchShopifyCollections, ShopifyCollection } from "@/lib/shopify";
import type { MenuSection } from "./types";

/**
 * Loads the header menu sections (cached in sessionStorage) and the raw
 * Shopify collections list. Mirrors the legacy behavior in Header.tsx.
 */
export function useHeaderMenu() {
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const [menuSections, setMenuSections] = useState<MenuSection[]>([]);
  const [isMenuLoading, setIsMenuLoading] = useState(true);

  useEffect(() => {
    const loadCollections = async () => {
      // Try to load from cache first for instant display
      const cachedMenu = sessionStorage.getItem("header_menu_cache");
      const cachedCollections = sessionStorage.getItem("header_collections_cache");

      if (cachedMenu && cachedCollections) {
        try {
          setMenuSections(JSON.parse(cachedMenu));
          setCollections(JSON.parse(cachedCollections));
          setIsMenuLoading(false);
        } catch {
          // ignore parse errors
        }
      }

      // Load fresh data in background
      const result = await fetchShopifyCollections();
      const collectionsData = result.collections;
      setCollections(collectionsData);

      try {
        sessionStorage.setItem(
          "header_collections_cache",
          JSON.stringify(collectionsData)
        );
      } catch {}

      const { data: headerSetting } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "header_collections")
        .maybeSingle();

      if (headerSetting?.setting_value) {
        const value = headerSetting.setting_value as {
          sections?: Array<{
            title: string;
            directCollectionHandle?: string;
            titleCollectionHandle?: string;
            titleCollectionHandles?: string[];
            imageUrl?: string;
            subsections: Array<{
              title: string;
              collections: string[];
              collectionTitles?: Record<string, string>;
            }>;
          }>;
        };

        if (value.sections && value.sections.length > 0) {
          const sections: MenuSection[] = value.sections.map((section) => {
            let sectionImage: MenuSection["image"] = null;

            if (section.imageUrl) {
              sectionImage = { url: section.imageUrl, altText: section.title };
            } else {
              const firstSubsection = section.subsections?.[0];
              const firstCollectionHandle = firstSubsection?.collections?.[0];
              const firstCollection = firstCollectionHandle
                ? collectionsData.find((c) => c.node.handle === firstCollectionHandle)
                : null;
              sectionImage = firstCollection?.node.image || null;
            }

            return {
              title: section.title,
              directCollectionHandle: section.directCollectionHandle,
              titleCollectionHandles:
                section.titleCollectionHandles ||
                (section.titleCollectionHandle ? [section.titleCollectionHandle] : undefined),
              image: sectionImage,
              subsections: section.subsections.map((subsection) => ({
                title: subsection.title,
                collections: subsection.collections
                  .map((handle) => collectionsData.find((c) => c.node.handle === handle))
                  .filter(Boolean) as ShopifyCollection[],
                collectionTitles: subsection.collectionTitles,
              })),
            };
          });

          setMenuSections(sections);

          try {
            sessionStorage.setItem("header_menu_cache", JSON.stringify(sections));
          } catch {}
        }
      }
      setIsMenuLoading(false);
    };

    loadCollections();
  }, []);

  return { collections, menuSections, isMenuLoading };
}
